/**
 * DDFA — Event Calendar Page
 * ES6 module — view toggling, filtering, calendar grid, modal, Google Calendar & mailto.
 * No dependencies. Loaded with type="module" defer.
 */

(function () {
  'use strict';

  /* =========================================================================
     DATA
     ========================================================================= */

  const dataEl = document.getElementById('calendar-events-data');
  const events = dataEl ? JSON.parse(dataEl.textContent) : [];

  /* =========================================================================
     DOM REFS
     ========================================================================= */

  const section = document.querySelector('[data-calendar-section]');
  if (!section) return;

  const listPanel = section.querySelector('[data-view-panel="list"]');
  const gridPanel = section.querySelector('[data-view-panel="calendar"]');
  const gridBody = section.querySelector('[data-grid-body]');
  const monthLabel = section.querySelector('[data-month-label]');
  const modal = section.querySelector('[data-modal]');
  const modalBody = section.querySelector('[data-modal-body]');
  const bookingModal = section.querySelector('[data-booking-modal]');
  const bookingForm = section.querySelector('[data-booking-form]');
  const bookingSuccess = section.querySelector('[data-booking-success]');

  /* Sort list view cards by date (metaobjects arrive in creation order) */
  if (listPanel) {
    const cards = [...listPanel.querySelectorAll('.ddfa-calendar__card')];
    cards.sort((a, b) => (a.dataset.eventDate || '').localeCompare(b.dataset.eventDate || ''));
    cards.forEach(card => listPanel.appendChild(card));
  }

  /* =========================================================================
     STATE
     ========================================================================= */

  const defaultView = section.dataset.defaultView || 'list';
  let currentView = defaultView;
  let activeFilter = 'all';
  let currentYear = new Date().getFullYear();
  let currentMonth = new Date().getMonth();
  let lastFocusedElement = null;

  /* =========================================================================
     CONSTANTS
     ========================================================================= */

  const MONTH_NAMES = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  const EVENT_TYPE_CLASSES = {
    'Art Class': 'art-class',
    'Gallery Opening': 'gallery-opening',
    'Plein Air': 'plein-air',
    'Pop-Up Show': 'pop-up-show'
  };

  /* =========================================================================
     UTILITIES
     ========================================================================= */

  /**
   * Parse a time string like "5:00 PM" into { hours, minutes } in 24h format.
   */
  function parseTime(str) {
    if (!str) return { hours: 0, minutes: 0 };
    const match = str.match(/(\d{1,2}):(\d{2})\s*(AM|PM)?/i);
    if (!match) return { hours: 0, minutes: 0 };

    let hours = parseInt(match[1], 10);
    const minutes = parseInt(match[2], 10);
    const period = (match[3] || '').toUpperCase();

    if (period === 'PM' && hours < 12) hours += 12;
    if (period === 'AM' && hours === 12) hours = 0;

    return { hours, minutes };
  }

  /**
   * Format a date + time string into Google Calendar format: YYYYMMDDTHHMMSS
   */
  function toGoogleDate(dateStr, timeStr) {
    const [year, month, day] = dateStr.split('-').map(Number);
    const { hours, minutes } = parseTime(timeStr);
    const yy = String(year);
    const mm = String(month).padStart(2, '0');
    const dd = String(day).padStart(2, '0');
    const hh = String(hours).padStart(2, '0');
    const mi = String(minutes).padStart(2, '0');
    return `${yy}${mm}${dd}T${hh}${mi}00`;
  }

  /**
   * Format a date string into a human-readable format.
   */
  function formatDate(dateStr) {
    const [year, month, day] = dateStr.split('-').map(Number);
    const date = new Date(year, month - 1, day);
    const weekday = date.toLocaleDateString('en-US', { weekday: 'long' });
    const monthName = MONTH_NAMES[month - 1];
    return `${weekday}, ${monthName} ${day}, ${year}`;
  }

  /**
   * Get CSS class suffix for an event type.
   */
  function typeClass(eventType) {
    return EVENT_TYPE_CLASSES[eventType] || 'art-class';
  }

  /**
   * Check if an event matches the active filter.
   */
  function matchesFilter(event) {
    return activeFilter === 'all' || event.event_type === activeFilter;
  }

  /* =========================================================================
     VIEW TOGGLING
     ========================================================================= */

  function switchView(view) {
    currentView = view;

    // Update toggle buttons
    section.querySelectorAll('[data-view]').forEach(btn => {
      const isActive = btn.dataset.view === view;
      btn.classList.toggle('is-active', isActive);
      btn.setAttribute('aria-selected', isActive ? 'true' : 'false');
    });

    // Show/hide panels
    if (listPanel) listPanel.style.display = view === 'list' ? '' : 'none';
    if (gridPanel) gridPanel.style.display = view === 'calendar' ? '' : 'none';

    // Render calendar grid when switching to calendar view
    if (view === 'calendar') {
      renderMonth(currentYear, currentMonth);
    }
  }

  /* =========================================================================
     FILTERING
     ========================================================================= */

  function setFilter(type) {
    activeFilter = type;

    // Update filter buttons
    section.querySelectorAll('[data-filter]').forEach(btn => {
      btn.classList.toggle('is-active', btn.dataset.filter === type);
    });

    // Filter list view cards
    if (listPanel) {
      listPanel.querySelectorAll('[data-event-type]').forEach(card => {
        const match = type === 'all' || card.dataset.eventType === type;
        card.classList.toggle('is-hidden', !match);
      });
    }

    // Re-render calendar grid with filter
    if (currentView === 'calendar') {
      renderMonth(currentYear, currentMonth);
    }
  }

  /* =========================================================================
     CALENDAR GRID
     ========================================================================= */

  function renderMonth(year, month) {
    if (!gridBody || !monthLabel) return;

    // Update month label
    monthLabel.textContent = `${MONTH_NAMES[month]} ${year}`;

    // Calculate grid parameters
    const firstDay = new Date(year, month, 1).getDay(); // 0=Sun
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const daysInPrevMonth = new Date(year, month, 0).getDate();
    const today = new Date();
    const isCurrentMonth = today.getFullYear() === year && today.getMonth() === month;
    const todayDate = today.getDate();

    // Get events for this month (filtered)
    const monthEvents = events.filter(ev => {
      if (!matchesFilter(ev)) return false;
      const [ey, em] = ev.date.split('-').map(Number);
      return ey === year && em === month + 1;
    });

    // Build cells
    const totalCells = 42; // 6 rows x 7 cols
    let html = '';

    for (let i = 0; i < totalCells; i++) {
      let dayNum;
      let isOutside = false;
      let cellDate;

      if (i < firstDay) {
        // Previous month
        dayNum = daysInPrevMonth - firstDay + i + 1;
        isOutside = true;
        const prevMonth = month === 0 ? 11 : month - 1;
        const prevYear = month === 0 ? year - 1 : year;
        cellDate = `${prevYear}-${String(prevMonth + 1).padStart(2, '0')}-${String(dayNum).padStart(2, '0')}`;
      } else if (i >= firstDay + daysInMonth) {
        // Next month
        dayNum = i - firstDay - daysInMonth + 1;
        isOutside = true;
        const nextMonth = month === 11 ? 0 : month + 1;
        const nextYear = month === 11 ? year + 1 : year;
        cellDate = `${nextYear}-${String(nextMonth + 1).padStart(2, '0')}-${String(dayNum).padStart(2, '0')}`;
      } else {
        // Current month
        dayNum = i - firstDay + 1;
        cellDate = `${year}-${String(month + 1).padStart(2, '0')}-${String(dayNum).padStart(2, '0')}`;
      }

      const isToday = isCurrentMonth && !isOutside && dayNum === todayDate;
      const classes = [
        'ddfa-calendar__day',
        isOutside ? 'ddfa-calendar__day--outside' : '',
        isToday ? 'ddfa-calendar__day--today' : ''
      ].filter(Boolean).join(' ');

      // Find events on this date
      const dayEvents = isOutside ? [] : monthEvents.filter(ev => ev.date === cellDate);

      let pillsHtml = '';
      dayEvents.forEach(ev => {
        const cls = typeClass(ev.event_type);
        pillsHtml += `<span class="ddfa-calendar__pill ddfa-calendar__pill--${cls}" data-open-modal="${ev.id}" title="${ev.title}">${ev.title}</span>`;
      });

      html += `<div class="${classes}"><span class="ddfa-calendar__day-number">${dayNum}</span>${pillsHtml}</div>`;
    }

    gridBody.innerHTML = html;
  }

  function navigateMonth(delta) {
    currentMonth += delta;
    if (currentMonth > 11) {
      currentMonth = 0;
      currentYear++;
    } else if (currentMonth < 0) {
      currentMonth = 11;
      currentYear--;
    }
    renderMonth(currentYear, currentMonth);
  }

  /* =========================================================================
     MODAL
     ========================================================================= */

  function openModal(eventId) {
    const event = events.find(e => e.id === eventId);
    if (!event || !modal || !modalBody) return;

    lastFocusedElement = document.activeElement;

    // Build modal content
    const cls = typeClass(event.event_type);
    const dateFormatted = formatDate(event.date);
    const locationDisplay = event.location_address
      ? `<a href="https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(event.location_address)}" target="_blank" rel="noopener noreferrer">${event.location_name}</a>`
      : event.location_name;

    // Price display
    let priceHtml = '';
    if (event.price && event.price > 0) {
      priceHtml = `<span class="ddfa-calendar__price">$${Number(event.price).toFixed(0)}</span>`;
    } else {
      priceHtml = '<span class="ddfa-calendar__price ddfa-calendar__price--free">Free Event</span>';
    }

    // Capacity
    let capacityHtml = '';
    if (event.max_capacity) {
      capacityHtml = `<span class="ddfa-calendar__capacity">${event.max_capacity} spots available</span>`;
    }

    // Google Calendar URL
    const gcalStart = toGoogleDate(event.date, event.start_time);
    const gcalEnd = toGoogleDate(event.date, event.end_time);
    const gcalLocation = event.location_address || event.location_name || '';
    const gcalUrl = `https://calendar.google.com/calendar/render?action=TEMPLATE&text=${encodeURIComponent(event.title)}&dates=${gcalStart}/${gcalEnd}&location=${encodeURIComponent(gcalLocation)}&details=${encodeURIComponent(event.description || event.short_description || '')}`;

    // Email reminder mailto
    const mailSubject = `Reminder: ${event.title}`;
    const mailBody = `Event: ${event.title}\nDate: ${dateFormatted}\nTime: ${event.start_time} – ${event.end_time}\nLocation: ${event.location_name}${event.location_address ? '\nAddress: ' + event.location_address : ''}\n\n${event.description || ''}`;
    const mailtoUrl = `mailto:?subject=${encodeURIComponent(mailSubject)}&body=${encodeURIComponent(mailBody)}`;

    // Register / RSVP button
    let registerHtml = '';
    if (event.registration_url) {
      const regLabel = event.price && event.price > 0
        ? `Register & Pay — $${Number(event.price).toFixed(0)}`
        : 'RSVP';
      registerHtml = `<a href="${event.registration_url}" class="ddfa-calendar__modal-btn ddfa-calendar__modal-btn--primary" target="_blank" rel="noopener noreferrer">
        <svg viewBox="0 0 16 16" fill="none" aria-hidden="true"><path d="M6 12L10 8L6 4" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>
        ${regLabel}
      </a>`;
    } else if (!event.price || event.price <= 0) {
      // Free event with no registration URL — RSVP via email
      registerHtml = `<a href="${mailtoUrl}" class="ddfa-calendar__modal-btn ddfa-calendar__modal-btn--primary">
        <svg viewBox="0 0 16 16" fill="none" aria-hidden="true"><path d="M6 12L10 8L6 4" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>
        RSVP
      </a>`;
    }

    let html = '';

    // Image
    if (event.image) {
      html += `<img class="ddfa-calendar__modal-image" src="${event.image}" alt="${event.title}" loading="lazy">`;
    }

    // Badge
    html += `<span class="ddfa-calendar__badge ddfa-calendar__badge--${cls}">${event.event_type}</span>`;

    // Title
    html += `<h2 class="ddfa-calendar__modal-title">${event.title}</h2>`;

    // Price & capacity
    html += `<div style="display:flex;align-items:center;gap:12px;margin-bottom:12px">${priceHtml}${capacityHtml}</div>`;

    // Meta
    html += `<div class="ddfa-calendar__modal-meta">`;
    html += `<div class="ddfa-calendar__modal-meta-row">
      <svg viewBox="0 0 16 16" fill="none" aria-hidden="true"><rect x="2" y="3" width="12" height="11" rx="1.5" stroke="currentColor" stroke-width="1.2"/><line x1="2" y1="7" x2="14" y2="7" stroke="currentColor" stroke-width="1.2"/><line x1="5.5" y1="1.5" x2="5.5" y2="4.5" stroke="currentColor" stroke-width="1.2" stroke-linecap="round"/><line x1="10.5" y1="1.5" x2="10.5" y2="4.5" stroke="currentColor" stroke-width="1.2" stroke-linecap="round"/></svg>
      ${dateFormatted}
    </div>`;
    html += `<div class="ddfa-calendar__modal-meta-row">
      <svg viewBox="0 0 16 16" fill="none" aria-hidden="true"><circle cx="8" cy="8" r="6" stroke="currentColor" stroke-width="1.2"/><path d="M8 4.5V8.5L10.5 10" stroke="currentColor" stroke-width="1.2" stroke-linecap="round"/></svg>
      ${event.start_time} – ${event.end_time}
    </div>`;
    html += `<div class="ddfa-calendar__modal-meta-row">
      <svg viewBox="0 0 16 16" fill="none" aria-hidden="true"><path d="M8 1.5C5.5 1.5 3.5 3.5 3.5 6C3.5 9.5 8 14.5 8 14.5S12.5 9.5 12.5 6C12.5 3.5 10.5 1.5 8 1.5Z" stroke="currentColor" stroke-width="1.2"/><circle cx="8" cy="6" r="1.5" stroke="currentColor" stroke-width="1.2"/></svg>
      ${locationDisplay}
    </div>`;
    html += `</div>`;

    // Description
    if (event.description) {
      html += `<div class="ddfa-calendar__modal-description">${formatDescription(event.description)}</div>`;
    }

    // Action buttons
    html += `<div class="ddfa-calendar__modal-actions">`;
    html += registerHtml;
    html += `<a href="${gcalUrl}" class="ddfa-calendar__modal-btn ddfa-calendar__modal-btn--secondary" target="_blank" rel="noopener noreferrer">
      <svg viewBox="0 0 16 16" fill="none" aria-hidden="true"><rect x="2" y="3" width="12" height="11" rx="1.5" stroke="currentColor" stroke-width="1.2"/><line x1="2" y1="7" x2="14" y2="7" stroke="currentColor" stroke-width="1.2"/><path d="M6 10h4" stroke="currentColor" stroke-width="1.2" stroke-linecap="round"/></svg>
      Add to Google Calendar
    </a>`;
    html += `<a href="${mailtoUrl}" class="ddfa-calendar__modal-btn ddfa-calendar__modal-btn--secondary">
      <svg viewBox="0 0 16 16" fill="none" aria-hidden="true"><rect x="1.5" y="3.5" width="13" height="9" rx="1.5" stroke="currentColor" stroke-width="1.2"/><path d="M1.5 4.5L8 9L14.5 4.5" stroke="currentColor" stroke-width="1.2" stroke-linecap="round" stroke-linejoin="round"/></svg>
      Email Me a Reminder
    </a>`;
    html += `</div>`;

    modalBody.innerHTML = html;

    // Show modal
    modal.setAttribute('aria-hidden', 'false');
    document.documentElement.classList.add('scroll-lock');

    // Focus the close button
    requestAnimationFrame(() => {
      const closeBtn = modal.querySelector('[data-modal-close]');
      if (closeBtn) closeBtn.focus();
    });
  }

  function closeModal() {
    if (!modal) return;
    modal.setAttribute('aria-hidden', 'true');
    document.documentElement.classList.remove('scroll-lock');

    // Restore focus
    if (lastFocusedElement) {
      lastFocusedElement.focus();
      lastFocusedElement = null;
    }
  }

  /**
   * Convert plain text description into paragraphs.
   * If it contains HTML tags, pass through. Otherwise, split on double newlines.
   */
  function formatDescription(text) {
    if (!text) return '';
    // If it already contains HTML block elements, pass through
    if (/<(p|div|ul|ol|h[1-6])\b/i.test(text)) return text;
    // Split on double newlines for paragraphs
    return text.split(/\n\n+/).map(p => `<p>${p.replace(/\n/g, '<br>')}</p>`).join('');
  }

  /* =========================================================================
     BOOKING MODAL
     ========================================================================= */

  function openBookingModal() {
    if (!bookingModal) return;
    lastFocusedElement = document.activeElement;

    // Reset form state for clean reopen
    if (bookingForm) {
      bookingForm.reset();
      bookingForm.style.display = '';
      var submitBtn = bookingForm.querySelector('.ddfa-calendar__booking-submit');
      if (submitBtn) { submitBtn.disabled = false; submitBtn.textContent = 'Request Appointment'; }
    }
    if (bookingSuccess) bookingSuccess.style.display = 'none';

    // Set date input min to today
    var dateInput = bookingModal.querySelector('[data-booking-date]');
    if (dateInput) {
      var today = new Date().toISOString().split('T')[0];
      dateInput.setAttribute('min', today);
    }

    bookingModal.setAttribute('aria-hidden', 'false');
    document.documentElement.classList.add('scroll-lock');

    requestAnimationFrame(function () {
      var closeBtn = bookingModal.querySelector('[data-booking-close]');
      if (closeBtn) closeBtn.focus();
    });
  }

  function closeBookingModal() {
    if (!bookingModal) return;
    bookingModal.setAttribute('aria-hidden', 'true');
    document.documentElement.classList.remove('scroll-lock');

    if (lastFocusedElement) {
      lastFocusedElement.focus();
      lastFocusedElement = null;
    }
  }

  function handleBookingSubmit(e) {
    e.preventDefault();

    var form = e.target;
    var visitType = form.querySelector('[data-booking-visit-type]').value;
    var dateVal = form.querySelector('[data-booking-date]').value;
    var timeVal = form.querySelector('[data-booking-time]').value;
    var messageVal = form.querySelector('[data-booking-message]').value;
    var phoneVal = form.querySelector('[data-booking-phone]').value;

    // Format date for readability
    var dateDisplay = 'Not specified';
    if (dateVal) {
      var d = new Date(dateVal + 'T00:00:00');
      dateDisplay = d.toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' });
    }

    // Build clean, labeled body for the email Drew receives
    var bodyParts = [
      '--- Studio Visit Request ---',
      '',
      'Visit Type: ' + visitType,
      'Preferred Date: ' + dateDisplay,
      'Preferred Time: ' + timeVal
    ];
    if (phoneVal.trim()) {
      bodyParts.push('Phone: ' + phoneVal.trim());
    }
    if (messageVal.trim()) {
      bodyParts.push('', 'Message:', messageVal.trim());
    }
    bodyParts.push('', '---');

    // Set the hidden body field
    form.querySelector('[data-booking-body]').value = bodyParts.join('\n');

    // Disable submit button
    var submitBtn = form.querySelector('.ddfa-calendar__booking-submit');
    if (submitBtn) {
      submitBtn.disabled = true;
      submitBtn.textContent = 'Sending...';
    }

    // Submit via fetch to avoid page redirect
    var formData = new FormData(form);
    fetch('/contact#contact_form', {
      method: 'POST',
      body: formData,
      headers: { 'Accept': 'application/json' }
    })
      .then(function () {
        // Show success (Shopify /contact always returns 200)
        form.style.display = 'none';
        if (bookingSuccess) bookingSuccess.style.display = 'block';
      })
      .catch(function () {
        // Fallback: submit normally (redirects to /contact page)
        form.submit();
      });
  }

  /* =========================================================================
     EVENT LISTENERS
     ========================================================================= */

  function init() {
    // Use event delegation on the section for all clicks
    section.addEventListener('click', function (e) {
      // View toggle
      const viewBtn = e.target.closest('[data-view]');
      if (viewBtn) {
        e.preventDefault();
        switchView(viewBtn.dataset.view);
        return;
      }

      // Filter button
      const filterBtn = e.target.closest('[data-filter]');
      if (filterBtn) {
        e.preventDefault();
        setFilter(filterBtn.dataset.filter);
        return;
      }

      // Month navigation
      if (e.target.closest('[data-month-prev]')) {
        e.preventDefault();
        navigateMonth(-1);
        return;
      }
      if (e.target.closest('[data-month-next]')) {
        e.preventDefault();
        navigateMonth(1);
        return;
      }

      // Open modal from card details button or calendar pill
      const modalTrigger = e.target.closest('[data-open-modal]');
      if (modalTrigger) {
        e.preventDefault();
        e.stopPropagation();
        openModal(modalTrigger.dataset.openModal);
        return;
      }

      // Open modal by clicking anywhere on a card (but not on links)
      const card = e.target.closest('.ddfa-calendar__card');
      if (card && !e.target.closest('a')) {
        e.preventDefault();
        openModal(card.dataset.eventId);
        return;
      }

      // Open booking modal
      if (e.target.closest('[data-open-booking]')) {
        e.preventDefault();
        openBookingModal();
        return;
      }

      // Close booking modal — overlay
      if (e.target.closest('[data-booking-overlay]')) {
        closeBookingModal();
        return;
      }

      // Close booking modal — close button
      if (e.target.closest('[data-booking-close]')) {
        closeBookingModal();
        return;
      }

      // Close modal — overlay click
      if (e.target.closest('[data-modal-overlay]')) {
        closeModal();
        return;
      }

      // Close modal — close button
      if (e.target.closest('[data-modal-close]')) {
        closeModal();
        return;
      }
    });

    // Close modals on Escape
    document.addEventListener('keydown', function (e) {
      if (e.key !== 'Escape') return;
      if (bookingModal && bookingModal.getAttribute('aria-hidden') === 'false') {
        closeBookingModal();
        return;
      }
      if (modal && modal.getAttribute('aria-hidden') === 'false') {
        closeModal();
      }
    });

    // Focus trap within modal
    modal && modal.addEventListener('keydown', function (e) {
      if (e.key !== 'Tab') return;
      const focusable = modal.querySelectorAll('a[href], button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])');
      if (focusable.length === 0) return;

      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      if (e.shiftKey) {
        if (document.activeElement === first) {
          e.preventDefault();
          last.focus();
        }
      } else {
        if (document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    });

    // Booking form submission
    if (bookingForm) {
      bookingForm.addEventListener('submit', handleBookingSubmit);
    }

    // Focus trap within booking modal
    bookingModal && bookingModal.addEventListener('keydown', function (e) {
      if (e.key !== 'Tab') return;
      var focusable = bookingModal.querySelectorAll(
        'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
      );
      if (focusable.length === 0) return;

      var first = focusable[0];
      var last = focusable[focusable.length - 1];

      if (e.shiftKey) {
        if (document.activeElement === first) {
          e.preventDefault();
          last.focus();
        }
      } else {
        if (document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    });

    // Set stagger animation indices on list cards
    if (listPanel) {
      const cards = listPanel.querySelectorAll('.ddfa-calendar__card');
      cards.forEach((card, i) => {
        card.style.setProperty('--index', i);
      });
    }

    // Render calendar grid if starting in calendar view
    if (currentView === 'calendar') {
      renderMonth(currentYear, currentMonth);
    }
  }

  /* =========================================================================
     BOOT
     ========================================================================= */

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
